import React, { useState, useEffect } from 'react';
import { api } from '@/utils/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  DollarSign, TrendingUp, Building2, AlertTriangle, Users,
  RefreshCw, Clock, Shield, Target, XCircle, CheckCircle,
  AlertCircle, Bell, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const CommandCenter = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('MTD');
  const [revenue, setRevenue] = useState(null);
  const [hygiene, setHygiene] = useState(null);
  const [risks, setRisks] = useState(null);
  const [alerts, setAlerts] = useState([]);

  const isSuperAdmin = user?.role === 'super_admin' || user?.role === 'Admin';

  useEffect(() => {
    fetchData();
  }, [period]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [revenueRes, hygieneRes, risksRes, alertsRes] = await Promise.all([
        api.getCommandCenter(null, period),
        api.getPipelineHygiene(),
        api.getRiskRadar(),
        api.getAdminAlerts()
      ]);
      setRevenue(revenueRes.data);
      setHygiene(hygieneRes.data);
      setRisks(risksRes.data);
      setAlerts(alertsRes.data?.alerts || []);
    } catch (error) {
      console.error('Failed to load command center:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const formatAV = (lacs) => {
    if (!lacs) return '₹0';
    if (lacs >= 100) return `₹${(lacs / 100).toFixed(2)} Cr`;
    return `₹${lacs.toFixed(2)} L`;
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'HIGH': return 'bg-red-100 text-red-700 border-red-200';
      case 'MEDIUM': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'LOW': return 'bg-blue-100 text-blue-700 border-blue-200';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  if (loading && !revenue) {
    return (
      <div className="min-h-screen bg-[#F7F8FA] flex items-center justify-center">
        <RefreshCw size={24} className="animate-spin text-[#5B3DF5]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F8FA] pb-20 md:pb-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white px-4 py-4 md:px-8 md:py-6">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-xl md:text-2xl font-bold">Command Center</h1>
              <p className="text-slate-400 text-sm">Real-time pipeline intelligence</p>
            </div>
            <div className="flex items-center gap-2">
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="w-[100px] h-9 bg-slate-800 border-slate-700 text-white rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="MTD">MTD</SelectItem>
                  <SelectItem value="QTD">QTD</SelectItem>
                  <SelectItem value="FYTD">FYTD</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchData}
                className="h-9 rounded-xl border-slate-700 text-white hover:bg-slate-700"
                disabled={loading}
              >
                <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              </Button>
            </div>
          </div>

          {/* Alerts Banner */}
          {alerts.length > 0 && (
            <div className="flex items-center gap-2 p-3 bg-[#5B3DF5]/20 rounded-xl border border-[#5B3DF5]/30">
              <Bell size={16} className="text-red-400" />
              <span className="text-sm font-medium">{alerts.length} alerts require attention</span>
              <ChevronRight size={14} className="ml-auto text-red-400" />
            </div>
          )}
        </div>
      </div>

      <div className="px-4 py-4 md:px-8 md:py-6">
        <div className="max-w-[1400px] mx-auto space-y-6">
          
          {/* SECTION 1: Revenue Command Center */}
          <div>
            <h2 className="font-bold text-[#111827] mb-3 flex items-center gap-2">
              <DollarSign size={18} className="text-emerald-500" />
              Revenue Command Center
            </h2>
            
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <Card className="p-4 bg-white shadow-sm rounded-2xl">
                <p className="text-xs text-slate-500 mb-1">Gross AV</p>
                <p className="text-2xl font-black text-[#111827]">{formatAV(revenue?.revenue?.gross_av_lacs)}</p>
                <p className="text-xs text-slate-400">{revenue?.bookings?.total || 0} bookings</p>
              </Card>
              
              <Card className="p-4 bg-white shadow-sm rounded-2xl">
                <p className="text-xs text-slate-500 mb-1">Verified AV</p>
                <p className="text-2xl font-black text-emerald-600">{formatAV(revenue?.revenue?.verified_av_lacs)}</p>
                <p className="text-xs text-slate-400">{revenue?.bookings?.approved || 0} approved</p>
              </Card>
              
              <Card className="p-4 bg-white shadow-sm rounded-2xl">
                <p className="text-xs text-slate-500 mb-1">Agreement AV</p>
                <p className="text-2xl font-black text-blue-600">{formatAV(revenue?.revenue?.agreement_av_lacs)}</p>
                <p className="text-xs text-slate-400">{revenue?.bookings?.agreement_done || 0} done</p>
              </Card>
              
              <Card className="p-4 bg-white shadow-sm rounded-2xl">
                <p className="text-xs text-slate-500 mb-1">Cancellation %</p>
                <p className={`text-2xl font-black ${revenue?.revenue?.cancellation_rate > 10 ? 'text-[#5B3DF5]' : 'text-slate-700'}`}>
                  {revenue?.revenue?.cancellation_rate || 0}%
                </p>
                <p className="text-xs text-slate-400">{revenue?.bookings?.cancelled || 0} cancelled</p>
              </Card>
              
              <Card className="p-4 bg-white shadow-sm rounded-2xl">
                <p className="text-xs text-slate-500 mb-1">Avg Ticket Size</p>
                <p className="text-2xl font-black text-purple-600">{formatAV(revenue?.revenue?.avg_ticket_size_lacs)}</p>
              </Card>
            </div>

            {/* City Contribution */}
            {revenue?.revenue?.city_contribution && Object.keys(revenue.revenue.city_contribution).length > 0 && (
              <Card className="mt-4 p-4 bg-white shadow-sm rounded-2xl">
                <h3 className="text-sm font-bold text-[#111827] mb-3">City Contribution %</h3>
                <div className="flex flex-wrap gap-3">
                  {Object.entries(revenue.revenue.city_contribution)
                    .sort(([,a], [,b]) => b - a)
                    .map(([city, percent]) => (
                    <div key={city} className="flex items-center gap-2">
                      <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-emerald-500 rounded-full"
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                      <span className="text-xs font-medium text-slate-600">{city}</span>
                      <span className="text-xs font-bold text-[#111827]">{percent}%</span>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>

          {/* SECTION 2: Pipeline Hygiene Monitor */}
          <div>
            <h2 className="font-bold text-[#111827] mb-3 flex items-center gap-2">
              <Target size={18} className="text-blue-500" />
              Pipeline Hygiene Monitor
            </h2>
            
            <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
              <Card className="p-4 bg-white shadow-sm rounded-2xl">
                <div className="flex items-center gap-2 mb-2">
                  <Users size={16} className="text-blue-500" />
                  <p className="text-xs text-slate-500">Active</p>
                </div>
                <p className="text-2xl font-black text-[#111827]">{hygiene?.active_customers || 0}</p>
              </Card>
              
              <Card className="p-4 bg-amber-50 border border-amber-100 rounded-2xl">
                <div className="flex items-center gap-2 mb-2">
                  <Clock size={16} className="text-amber-500" />
                  <p className="text-xs text-amber-700">Expiring in 7d</p>
                </div>
                <p className="text-2xl font-black text-amber-700">{hygiene?.expiring_in_7_days || 0}</p>
              </Card>
              
              <Card className="p-4 bg-red-50 border border-red-100 rounded-2xl">
                <div className="flex items-center gap-2 mb-2">
                  <XCircle size={16} className="text-[#5B3DF5]" />
                  <p className="text-xs text-red-700">VDNB Count</p>
                </div>
                <p className="text-2xl font-black text-red-700">{hygiene?.vdnb_count || 0}</p>
              </Card>
              
              <Card className="p-4 bg-white shadow-sm rounded-2xl">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp size={16} className="text-slate-500" />
                  <p className="text-xs text-slate-500">VDNB %</p>
                </div>
                <p className={`text-2xl font-black ${hygiene?.vdnb_percent > 20 ? 'text-[#5B3DF5]' : 'text-slate-700'}`}>
                  {hygiene?.vdnb_percent || 0}%
                </p>
              </Card>
              
              <Card className="p-4 bg-white shadow-sm rounded-2xl">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle size={16} className="text-orange-500" />
                  <p className="text-xs text-slate-500">Inactive &gt;10d</p>
                </div>
                <p className="text-2xl font-black text-orange-600">{hygiene?.inactive_over_10_days || 0}</p>
                <p className="text-[10px] text-slate-400">{hygiene?.inactive_percent || 0}% of active</p>
              </Card>
              
              <Card 
                className="p-4 bg-slate-900 text-white rounded-2xl cursor-pointer hover:bg-slate-800 transition-colors"
                onClick={() => navigate('/admin/vdnb')}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Shield size={16} className="text-emerald-400" />
                  <p className="text-xs text-slate-400">Manage VDNB</p>
                </div>
                <p className="text-sm font-medium">View Pool →</p>
              </Card>
            </div>

            {/* City Hygiene Scores */}
            {hygiene?.city_hygiene_scores && Object.keys(hygiene.city_hygiene_scores).length > 0 && (
              <Card className="mt-4 p-4 bg-white shadow-sm rounded-2xl">
                <h3 className="text-sm font-bold text-[#111827] mb-3">City Hygiene Scores</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {Object.entries(hygiene.city_hygiene_scores)
                    .sort(([,a], [,b]) => b - a)
                    .map(([city, score]) => (
                    <div key={city} className="p-3 bg-slate-50 rounded-xl text-center">
                      <p className="text-xs text-slate-500 mb-1">{city}</p>
                      <p className={`text-xl font-black ${
                        score >= 80 ? 'text-emerald-600' :
                        score >= 60 ? 'text-amber-600' : 'text-[#5B3DF5]'
                      }`}>
                        {score}
                      </p>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>

          {/* SECTION 3: Risk Radar */}
          <div>
            <h2 className="font-bold text-[#111827] mb-3 flex items-center gap-2">
              <AlertTriangle size={18} className="text-[#5B3DF5]" />
              Risk Radar
              {risks?.total_risks > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full font-medium">
                  {risks.total_risks} issues
                </span>
              )}
            </h2>
            
            {/* Severity Summary */}
            <div className="grid grid-cols-3 gap-4 mb-4">
              <Card className="p-3 bg-red-50 border border-red-100 rounded-xl">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-red-700">High</span>
                  <span className="text-lg font-black text-red-700">{risks?.by_severity?.high || 0}</span>
                </div>
              </Card>
              <Card className="p-3 bg-amber-50 border border-amber-100 rounded-xl">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-amber-700">Medium</span>
                  <span className="text-lg font-black text-amber-700">{risks?.by_severity?.medium || 0}</span>
                </div>
              </Card>
              <Card className="p-3 bg-blue-50 border border-blue-100 rounded-xl">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-blue-700">Low</span>
                  <span className="text-lg font-black text-blue-700">{risks?.by_severity?.low || 0}</span>
                </div>
              </Card>
            </div>

            {/* Risk List */}
            {risks?.risks?.length > 0 ? (
              <Card className="bg-white shadow-sm rounded-2xl overflow-hidden">
                <div className="divide-y divide-slate-100">
                  {risks.risks.slice(0, 10).map((risk, index) => (
                    <div key={index} className={`p-4 ${getSeverityColor(risk.severity)} bg-opacity-50`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${getSeverityColor(risk.severity)}`}>
                              {risk.severity}
                            </span>
                            <span className="text-xs font-medium text-slate-600">{risk.type.replace(/_/g, ' ')}</span>
                          </div>
                          <p className="text-sm text-[#111827]">{risk.message}</p>
                          {risk.details && (
                            <div className="mt-1 text-xs text-slate-500">
                              {JSON.stringify(risk.details).substring(0, 100)}...
                            </div>
                          )}
                        </div>
                        <ChevronRight size={16} className="text-slate-400 mt-1" />
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            ) : (
              <Card className="p-8 bg-white shadow-sm rounded-2xl text-center">
                <CheckCircle size={32} className="mx-auto text-emerald-500 mb-2" />
                <p className="text-slate-500">No risks detected</p>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommandCenter;
